import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Typography,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import CloseIcon from '@material-ui/icons/Close';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Field, Form, Formik } from 'formik';
// import * as Yup from 'yup';
import { OrderDto, updateOrderBranch } from '../../../api/orders';
import { BranchDto, getAllBranches } from '../../../api/branches';
import FormikTextField from '../../atoms/FormikInput/FormikInput';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        dialogTitle: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        closeIcon: { cursor: 'pointer' },
        formControl: {
            minWidth: 120,
            width: '100%',
        },
        mt5: {
            marginBottom: theme.spacing(3),
            width: '100%',
        },
        checkList: {
            marginTop: theme.spacing(6),
        },
        progress: {
            height: '4px',
            width: '100%',
            marginBottom: '20px',
        },
        container: {
            position: 'relative',
            alignItems: 'center',
        },
        title: {
            width: '100%',
            paddingLeft: theme.spacing(2),
        },
    })
);

interface Props {
    onClose: () => void;
    open: boolean;
    order: OrderDto;
    onSuccess: () => void;
}
function initFilterForm() {
    return {
        branchFilter: '',
    };
}

export const OrderBranchEditDialog = ({ onClose, open, order, onSuccess }: Props) => {
    const classes = useStyles();
    const [item] = useState(initFilterForm());
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setLoading] = useState(false);
    const [isBranchesLoading, setBranchesLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedBranch, setSelectedBranch] = useState<string>('');
    const [branches, setBranches] = useState<BranchDto[]>([]);
    // const schema = () =>
    //     Yup.object().shape({
    //         branchFilter: Yup.string().required('Введите код или название отделения'),
    //     });

    const loadAllBranches = useCallback(async () => {
        try {
            setBranchesLoading(true);
            const branchList = await getAllBranches(search);
            setBranches(branchList?.content);
            if (search) {
                enqueueSnackbar('Список обновлен!', {
                    variant: 'success',
                });
            }
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
            setBranchesLoading(false);
        }
    }, [enqueueSnackbar, search]);

    const handleBranchChange = useCallback((e: any) => {
        const value = e.target.value as string;
        setSelectedBranch(value);
    }, []);

    const handleBranchChangeSubmit = useCallback(async () => {
        try {
            setLoading(true);
            await updateOrderBranch({ order_id: order.id, branch_code: selectedBranch });
            setLoading(false);
            enqueueSnackbar('Данные успешно сохранены', { variant: 'success' });
            setSelectedBranch('');
            onSuccess();
        } catch (e) {
            enqueueSnackbar(`Ошибка при сохранении данных: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
            setLoading(false);
        }
    }, [order, selectedBranch, enqueueSnackbar, onSuccess]);

    const handleDialogClose = useCallback(() => {
        setSearch('');
        onClose();
    }, [onClose]);

    useEffect(() => {
        loadAllBranches();
    }, [loadAllBranches]);
    return (
        <Dialog open={open} fullWidth={true} onClose={handleDialogClose}>
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <Typography variant={'h3'}>Сменить филиал</Typography>
                <CloseIcon className={classes.closeIcon} onClick={onClose} />
            </DialogTitle>
            <DialogContent>
                <Formik
                    enableReinitialize={true}
                    initialValues={item}
                    // validationSchema={schema()}
                    onSubmit={(values, { resetForm }) => {
                        const { branchFilter } = values;
                        setSearch(branchFilter);
                        resetForm();
                    }}
                >
                    {({ submitForm }) => (
                        <Form>
                            <Grid className={classes.container} container spacing={1}>
                                <Grid xs={8} item>
                                    <Field
                                        component={FormikTextField}
                                        className={classes.mt5}
                                        name="branchFilter"
                                        label="Введите код или наименование"
                                    />
                                </Grid>
                                <Grid xs={4} item>
                                    <Button
                                        disabled={isBranchesLoading}
                                        onClick={submitForm}
                                        variant="contained"
                                        color="primary"
                                    >
                                        Обновить список
                                    </Button>
                                </Grid>
                                <div className={classes.progress}>
                                    {isBranchesLoading && <LinearProgress />}
                                </div>
                            </Grid>
                        </Form>
                    )}
                </Formik>
                <FormControl
                    className={classes.formControl}
                    disabled={branches?.length < 1}
                    variant="outlined"
                >
                    <InputLabel id="branch-id-label">Выберите отделение</InputLabel>
                    <Select
                        labelId="branch-id-label"
                        id="branch-id"
                        value={selectedBranch}
                        label="Выберите отделение"
                        onChange={handleBranchChange}
                    >
                        {branches?.length && (
                            <MenuItem value="">
                                <em>Не выбрано</em>
                            </MenuItem>
                        )}

                        {branches?.map((branch, index) => (
                            <MenuItem key={`${branch.id}_${index}`} value={branch.code}>
                                {branch.title}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            {isLoading && <LinearProgress />}
            <DialogActions>
                <Button
                    onClick={handleBranchChangeSubmit}
                    disabled={!selectedBranch}
                    variant="contained"
                    color="primary"
                >
                    Сменить
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrderBranchEditDialog;
